/**
 * CompanyQA — Iter 102. Universal internal Q&A.
 * Route: /app/company-qa
 *
 * Layout:
 *   - Ask sandbox (any qa.ask user can use)
 *   - Source controls (qa.manage only)
 *   - Custom KB manager (qa.manage only)
 *   - Recent asks log (qa.manage only)
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { BookOpen, Search, Trash2, ThumbsUp, ThumbsDown, ExternalLink, Sparkles, Loader2, FileText, Shield } from "lucide-react";
import SmartRichText from "@/components/SmartRichText";

const SOURCE_LABEL = {
  policies: "Company policies",
  engagement: "Bulletin engagement archive",
  helpdesk_kb: "Helpdesk knowledge base",
  custom: "Custom Q&A training files",
};
const SOURCE_HINT = {
  policies: "Published policy documents from /app/policies.",
  engagement: "Quotes, news, tips published on the bulletin (last 90 days).",
  helpdesk_kb: "Docs uploaded for helpdesk deflection (Helpdesk → Settings → AI & KB).",
  custom: "Files uploaded here (below) exclusively for Q&A. Best for HR handbooks, benefits, internal FAQs.",
};

export default function CompanyQAPage() {
  const { activeWorkspaceId: wsId } = useAuth();
  const { can } = useWorkspacePerms();
  const canManage = can("qa.manage");
  const [settings, setSettings] = useState(null);
  const load = async () => {
    if (!wsId || !canManage) return;
    try {
      const r = await api.get(`/workspaces/${wsId}/qa/settings`);
      setSettings(r.data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, [wsId, canManage]);

  return (
    <div className="space-y-4" data-testid="company-qa-page">
      <Toaster richColors position="top-center" />
      <PageHeader title="Company Q&A"
                   subtitle="Universal search over your policies, bulletin archive, helpdesk KB & custom training files. Grounded answers only — no hallucinations."
                   icon={BookOpen} />

      <AskSandbox wsId={wsId} sources={settings?.sources} />

      {canManage && settings && (
        <>
          <SourceControls wsId={wsId} settings={settings} onSaved={load} />
          <CustomKbManager wsId={wsId} />
          <FaqDashboard wsId={wsId} />
          <AskLogViewer wsId={wsId} />
        </>
      )}
    </div>
  );
}

// ─────────────────────── Ask sandbox ───────────────────────

function AskSandbox({ wsId }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const ask = async () => {
    if (!q.trim() || !wsId) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.post(`/workspaces/${wsId}/qa/ask`, { question: q });
      setResult(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Ask failed")); }
    finally { setBusy(false); }
  };
  const feedback = async (helpful) => {
    if (!result?.log_id) return;
    try {
      await api.post(`/qa/ask-log/${result.log_id}/feedback`, { helpful });
      toast.success(helpful ? "Thanks — logged as helpful" : "Thanks — we'll review");
      setResult({ ...result, _rated: helpful });
    } catch (e) { toast.error(apiErrorMessage(e, "Feedback failed")); }
  };

  return (
    <div className="border border-border p-4 bg-gradient-to-br from-slate-50 to-white"
         data-testid="qa-ask-sandbox">
      <div className="text-[11px] uppercase tracking-widest font-semibold mb-2 flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5" /> Ask anything
      </div>
      <div className="flex gap-2">
        <Input placeholder="e.g. How many leave days do I have per year?"
               value={q} onChange={(e) => setQ(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && ask()}
               data-testid="qa-ask-input" />
        <Button size="sm" className="rounded-none" onClick={ask} disabled={busy || !q.trim()}
                data-testid="qa-ask-submit">
          <Search className="w-3.5 h-3.5 mr-1" /> {busy ? "Thinking…" : "Ask"}
        </Button>
      </div>

      {result && (
        <div className="mt-3 border border-border bg-background p-3" data-testid="qa-ask-result">
          {!result.grounded ? (
            <div className="text-sm text-slate-700">
              <div className="font-semibold text-indigo-700 mb-1">✨ We got your question.</div>
              We couldn&apos;t find a confident match in the currently enabled sources yet.
              This question is logged &mdash; an admin will train the assistant with the right material and you&apos;ll get better answers over time.
            </div>
          ) : (
            <>
              <div className="text-sm text-slate-800 whitespace-pre-wrap"
                   data-testid="qa-ask-answer">{result.answer}</div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Confidence {(result.confidence * 100).toFixed(0)}%</span>
                {(result.sources || []).filter((s) => s.cited).slice(0, 5).map((s, i) => (
                  <a key={i} href={s.ref_url}
                     className="text-[10px] px-2 py-0.5 border border-border hover:bg-muted rounded-sm inline-flex items-center gap-1"
                     data-testid={`qa-source-${i}`}>
                    {s.source_label}
                    <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                ))}
                {!result._rated && (
                  <div className="ml-auto flex items-center gap-1">
                    <button onClick={() => feedback(true)} data-testid="qa-fb-helpful"
                             className="text-[11px] px-2 py-0.5 border border-border hover:bg-emerald-50 hover:border-emerald-300 inline-flex items-center gap-1">
                      <ThumbsUp className="w-3 h-3" /> Helpful
                    </button>
                    <button onClick={() => feedback(false)} data-testid="qa-fb-unhelpful"
                             className="text-[11px] px-2 py-0.5 border border-border hover:bg-red-50 hover:border-red-300 inline-flex items-center gap-1">
                      <ThumbsDown className="w-3 h-3" /> Not helpful
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────── Source controls ───────────────────────

function SourceControls({ wsId, settings, onSaved }) {
  const [saving, setSaving] = useState(false);
  const toggleSource = async (key, value) => {
    setSaving(true);
    try {
      await api.patch(`/workspaces/${wsId}/qa/settings`,
                        { sources: { [key]: value } });
      onSaved && onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };
  return (
    <div className="border border-border p-3" data-testid="qa-sources">
      <div className="text-[11px] uppercase tracking-widest font-semibold mb-2">Sources</div>
      <div className="text-[11px] text-muted-foreground mb-3">
        Choose what the Q&A bot is allowed to reference. Disabled sources are strictly excluded from retrieval &mdash; the AI can never cite them.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {Object.keys(SOURCE_LABEL).map((k) => (
          <label key={k}
                  className="border border-border p-2 flex items-start gap-2 text-sm hover:bg-muted/30 cursor-pointer">
            <input type="checkbox" className="mt-1"
                    checked={!!(settings.sources || {})[k]}
                    disabled={saving}
                    onChange={(e) => toggleSource(k, e.target.checked)}
                    data-testid={`qa-source-toggle-${k}`} />
            <div>
              <div className="font-semibold">{SOURCE_LABEL[k]}</div>
              <div className="text-[10px] text-muted-foreground">{SOURCE_HINT[k]}</div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────── Custom KB manager ───────────────────────

function CustomKbManager({ wsId }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/workspaces/${wsId}/qa/docs`);
      setDocs(r.data);
    } catch { /* silent */ }
  };
  useEffect(() => { if (wsId) load(); }, [wsId]);

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f); fd.append("title", f.name);
      await api.post(`/workspaces/${wsId}/qa/docs/file`, fd,
                      { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${f.name}`);
      load();
    } catch (err) { toast.error(apiErrorMessage(err, "Upload failed")); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const addUrl = async () => {
    if (!urlInput.trim()) return;
    setUploading(true);
    try {
      await api.post(`/workspaces/${wsId}/qa/docs/url`, { url: urlInput.trim() });
      toast.success("URL indexed");
      setUrlInput(""); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setUploading(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}"? The Q&A bot will lose this reference.`)) return;
    try { await api.delete(`/qa/docs/${d.id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <div className="border border-border p-3" data-testid="qa-custom-kb">
      <div className="text-[11px] uppercase tracking-widest font-semibold mb-1">Custom Q&A training files</div>
      <div className="text-[11px] text-muted-foreground mb-3">
        Upload employee-handbook style docs, benefits guides, HR FAQ &mdash; anything you want the Q&A bot to know that isn&apos;t already in Policies or the Helpdesk KB.
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <label className="text-xs font-semibold border border-border px-3 py-2 cursor-pointer hover:bg-muted/50"
                data-testid="qa-kb-upload-btn">
          {uploading ? "Uploading…" : "Upload file"}
          <input type="file" hidden onChange={uploadFile} disabled={uploading}
                  accept=".pdf,.docx,.txt,.md,.html,.htm,.csv,.xlsx,.xls" />
        </label>
        <div className="flex items-center gap-1 flex-1 min-w-[240px]">
          <Input placeholder="https://intranet.example.com/handbook" value={urlInput}
                 onChange={(e) => setUrlInput(e.target.value)}
                 data-testid="qa-kb-url-input" />
          <Button size="sm" className="rounded-none" onClick={addUrl} disabled={uploading || !urlInput.trim()}
                  data-testid="qa-kb-url-add">Add URL</Button>
        </div>
      </div>
      <div className="border border-border divide-y divide-border max-h-72 overflow-y-auto" data-testid="qa-kb-list">
        {docs.length === 0 ? (
          <div className="p-3 text-xs text-muted-foreground">No custom docs yet. Upload one to train the Q&A bot.</div>
        ) : docs.map((d) => (
          <div key={d.id} className="p-2 flex items-center gap-2 text-xs" data-testid={`qa-kb-row-${d.id}`}>
            <span className="text-[9px] font-bold uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5">{d.kind}</span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{d.title}</div>
              <div className="text-[10px] text-muted-foreground">{d.chunk_count} chunks · {(d.size_bytes / 1024).toFixed(1)} KB · {d.status}</div>
            </div>
            <Button size="sm" variant="outline" className="rounded-none h-6 text-[10px] text-red-600"
                     onClick={() => remove(d)} data-testid={`qa-kb-del-${d.id}`}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────── Ask log review ───────────────────────

function AskLogViewer({ wsId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/qa/ask-log?days=30&limit=50`)
      .then((r) => setData(r.data)).catch(() => {});
  }, [wsId]);
  if (!data) return null;
  return (
    <div className="border border-border p-3" data-testid="qa-ask-log">
      <div className="text-[11px] uppercase tracking-widest font-semibold mb-2">Recent employee questions (last 30d)</div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
        <Tile label="Total" value={data.total} />
        <Tile label="Answered" value={data.answered} />
        <Tile label="👍 Helpful" value={data.helpful} />
        <Tile label="👎 Not helpful" value={data.unhelpful} />
        <Tile label="Helpful rate" value={`${data.helpful_rate}%`} />
      </div>
      <div className="border border-border divide-y divide-border max-h-80 overflow-y-auto">
        {(data.rows || []).length === 0 ? (
          <div className="p-3 text-xs text-muted-foreground">Nothing asked yet.</div>
        ) : (data.rows || []).map((r) => (
          <div key={r.id} className="p-2 text-xs">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold">{r.user_name}</span>
              <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase ${
                r.helpful === true ? "bg-emerald-100 text-emerald-700"
                : r.helpful === false ? "bg-red-100 text-red-700"
                : "bg-slate-100 text-slate-600"
              }`}>{r.helpful === true ? "Helpful" : r.helpful === false ? "Not helpful" : "No FB"}</span>
              <span className="text-[10px] text-muted-foreground ml-auto">
                {new Date(r.created_at).toLocaleString()} · conf {r.confidence}
              </span>
            </div>
            <div className="font-semibold">{r.question}</div>
            <div className="text-muted-foreground line-clamp-2 mt-0.5">{(r.answer || r.notes || "").slice(0, 300)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="border border-border p-2 bg-muted/20">
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-lg font-bold">{value}</div>
    </div>
  );
}


// ─────────────────────── FAQ / Training-gap dashboard ───────────────────────

function FaqDashboard({ wsId }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(60);
  const [draftFor, setDraftFor] = useState(null); // { question, related }
  const load = async () => {
    try {
      const r = await api.get(`/workspaces/${wsId}/qa/faq-dashboard?days=${days}`);
      setData(r.data);
    } catch { /* silent */ }
  };
  useEffect(() => { if (wsId) load(); }, [wsId, days]);
  if (!data) return null;
  const t = data.tiles || {};
  return (
    <div className="border border-border p-3" data-testid="qa-faq-dashboard">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-widest font-semibold">FAQ &amp; training intelligence</div>
        <div className="flex gap-1">
          {[7, 30, 60, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)}
                    data-testid={`qa-faq-days-${d}`}
                    className={`text-[11px] px-2 py-1 border border-border ${
                      days === d ? "bg-primary text-primary-foreground" : "bg-background"
                    }`}>{d}d</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3" data-testid="qa-faq-tiles">
        <Tile label="Total asks" value={t.total || 0} />
        <Tile label="Answered" value={t.answered || 0} />
        <Tile label="Helpful" value={t.helpful || 0} />
        <Tile label="Unhelpful" value={t.unhelpful || 0} />
        <Tile label="Low conf." value={t.low_confidence || 0} />
        <Tile label="Coverage" value={`${t.coverage_rate || 0}%`} />
      </div>

      {(t.ai_drafted_docs > 0 || t.ai_drafted_used_in_answers > 0) && (
        <div className="mb-3 border border-violet-300/40 bg-violet-500/5 dark:bg-violet-950/20 p-3" data-testid="qa-faq-ai-loop-tile">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-violet-700 dark:text-violet-300 mb-1">
            <Sparkles className="w-3 h-3" /> AI-drafted knowledge loop
          </div>
          <div className="flex items-baseline gap-4 flex-wrap">
            <div>
              <span className="text-2xl font-bold" data-testid="qa-faq-ai-drafted-count">{t.ai_drafted_docs}</span>
              <span className="text-xs text-muted-foreground ml-1.5">articles drafted from gaps</span>
            </div>
            <div>
              <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400" data-testid="qa-faq-ai-used-count">{t.ai_drafted_used_in_answers}</span>
              <span className="text-xs text-muted-foreground ml-1.5">used in answers ({data.window_days || 60}d)</span>
            </div>
            {t.ai_drafted_docs > 0 && (
              <div className="text-[10px] text-muted-foreground ml-auto">
                Close-rate: <strong>{Math.round((t.ai_drafted_used_in_answers / t.ai_drafted_docs) * 100)}%</strong>
              </div>
            )}
          </div>
        </div>
      )}

      {(data.action_items || []).length > 0 && (
        <div className="mb-3 space-y-1" data-testid="qa-faq-actions">
          {(data.action_items || []).map((a, i) => (
            <div key={i} data-testid={`qa-faq-action-${a.kind}`}
                  className={`text-xs px-3 py-2 border-l-4 ${
                    a.severity === "high" ? "border-red-500 bg-red-50 text-red-800"
                    : a.severity === "medium" ? "border-amber-500 bg-amber-50 text-amber-800"
                    : "border-sky-500 bg-sky-50 text-sky-800"
                  }`}>
              <span className="text-[9px] uppercase font-bold tracking-widest mr-2">{a.severity}</span>{a.text}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Most-asked questions</div>
          <div className="border border-border divide-y divide-border max-h-64 overflow-y-auto" data-testid="qa-faq-frequent">
            {(data.frequent_questions || []).length === 0 ? (
              <div className="p-2 text-xs text-muted-foreground">Nothing asked yet in this window.</div>
            ) : (data.frequent_questions || []).slice(0, 8).map((f, i) => (
              <div key={i} className="p-2 text-xs">
                <div className="font-semibold truncate">{f.sample_question}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  x{f.count} · {f.askers} asker(s) · avg conf {f.avg_conf}
                  {f.unanswered_count > 0 && <span className="ml-2 text-red-600 font-semibold">· {f.unanswered_count} unanswered</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-red-700 mb-1">Training queue — questions that need more source material</div>
          <div className="border border-red-200 divide-y divide-border max-h-64 overflow-y-auto bg-red-50/30" data-testid="qa-faq-gaps">
            {(data.training_gaps || []).length === 0 ? (
              <div className="p-2 text-xs text-muted-foreground">🎉 No coverage gaps — great job.</div>
            ) : (data.training_gaps || []).slice(0, 8).map((g, i) => (
              <div key={i} className="p-2 text-xs flex items-start gap-2" data-testid={`qa-faq-gap-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{g.sample_question}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    x{g.count} · unanswered {g.unanswered_count} · unhelpful {g.unhelpful_count} · low-conf {g.low_conf_count}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setDraftFor({ question: g.sample_question, related: (g.questions || []).filter((q) => q && q !== g.sample_question) })}
                  className="shrink-0 text-[10px] px-2 py-1 border border-violet-400/50 bg-violet-500/10 text-violet-700 hover:bg-violet-500/20 whitespace-nowrap"
                  data-testid={`qa-faq-gap-draft-${i}`}
                >
                  ✨ Draft article
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      {draftFor && (
        <QaDraftDialog
          wsId={wsId}
          question={draftFor.question}
          related={draftFor.related}
          onClose={() => setDraftFor(null)}
          onSaved={() => { setDraftFor(null); load(); }}
        />
      )}
    </div>
  );
}


// ─────────────────────── AI Draft Dialog (Iter 116) ───────────────────────

function QaDraftDialog({ wsId, question, related, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null); // { title, body_html, summary, category, tags, suggested_destination }
  const [destination, setDestination] = useState("kb");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const generate = async (destinationHint = "auto") => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/qa/draft-doc`, {
        question, related_questions: related || [], destination: destinationHint,
      });
      setDraft(data);
      setDestination(data.suggested_destination || "kb");
      setTitle(data.title || "");
      setBody(data.body_html || "");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Draft failed"));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { generate("auto"); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/qa/save-drafted-doc`, {
        destination,
        title: title.trim(),
        body_html: body,
        summary: draft?.summary || "",
        category: draft?.category || "general",
        tags: draft?.tags || [],
      });
      toast.success(destination === "policy" ? "Saved as draft Policy" : "Saved to Q&A knowledge base");
      onSaved?.(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl rounded-none" data-testid="qa-draft-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-violet-600" /> AI-drafted document
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Source question</div>
          <div className="border border-border p-2 text-xs bg-muted/40" data-testid="qa-draft-source-question">&ldquo;{question}&rdquo;</div>

          {busy && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-6 justify-center" data-testid="qa-draft-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Claude is drafting your article…
            </div>
          )}

          {!busy && draft && (
            <>
              <div className="border border-violet-200 bg-violet-50/40 dark:bg-violet-950/20 p-2 text-[11px] text-violet-800 dark:text-violet-300 flex items-center gap-2" data-testid="qa-draft-suggestion">
                Claude suggests saving this as a
                <strong className="uppercase">{draft.suggested_destination}</strong>. You can override below.
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Destination</div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setDestination("policy")}
                    className={`flex-1 border p-2 text-xs text-left ${destination === "policy" ? "border-primary bg-primary/10" : "border-border hover:bg-muted"}`}
                    data-testid="qa-draft-dest-policy">
                    <div className="flex items-center gap-1.5 font-semibold"><Shield className="w-3.5 h-3.5" /> Policy</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">Formal rules that employees must acknowledge.</div>
                  </button>
                  <button type="button" onClick={() => setDestination("kb")}
                    className={`flex-1 border p-2 text-xs text-left ${destination === "kb" ? "border-primary bg-primary/10" : "border-border hover:bg-muted"}`}
                    data-testid="qa-draft-dest-kb">
                    <div className="flex items-center gap-1.5 font-semibold"><FileText className="w-3.5 h-3.5" /> KB article</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">How-to guide indexed for future Q&A answers.</div>
                  </button>
                </div>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Title</div>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} className="h-9 rounded-none" data-testid="qa-draft-title" />
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Content</div>
                <SmartRichText
                  value={body}
                  onChange={setBody}
                  workspaceId={wsId}
                  mediaMode="url-only"
                  minRows={8}
                  maxHeight={420}
                  testid="qa-draft-body"
                />
              </div>

              <div className="text-[10px] text-muted-foreground flex flex-wrap gap-1.5">
                <strong>Category:</strong> {draft.category}
                {draft.tags?.length > 0 && <><strong className="ml-2">Tags:</strong> {draft.tags.join(", ")}</>}
              </div>
            </>
          )}
        </div>
        <DialogFooter className="flex-wrap gap-2 justify-between">
          <Button variant="outline" onClick={() => generate("auto")} disabled={busy || saving} className="rounded-none" data-testid="qa-draft-regenerate">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Regenerate
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="rounded-none" data-testid="qa-draft-cancel">Cancel</Button>
            <Button onClick={save} disabled={busy || saving || !title.trim() || !body.trim()} className="rounded-none" data-testid="qa-draft-save">
              {saving ? (<><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving…</>) : <>Save as {destination === "policy" ? "Policy draft" : "KB article"}</>}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

